/**
 * Strategy's start (Figma 13 A01 6:9410, tablet T00 107:20424): the hero,
 * the prompt box with the Strategy pill and its Glow, and the brand's
 * strategies as cards. One line goes to Creo as the first message (D44: sent,
 * not prefilled), and Creo creates the strategy itself, taking the period from
 * the words (a year from next month when they name none). A PDF can be added
 * with +. Nothing here promises the approach card or "Start writing": they have
 * no backend.
 */
import { useMemo, useRef, useState } from "react";
import { ArrowUp, FileText, Plus, X } from "lucide-react";
import type { StrategyListItem } from "@/types/strategy.type";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";
import { useChatModels } from "@/hooks/useChatModels";
import { useChatModelStore } from "@/stores/chatModelStore";
import { cn } from "@/utils/helpers";
import strategyPill from "@/assets/images/strategy/strategy-pill.jpg";
import { KitButton } from "../../components/kit/Button";
import { useModelPicker } from "../../agent/rail/ModelPicker";
import { IMPORT_MAX_BYTES, isImportableStrategyFile } from "../api/importStrategyDocument";
import { OBJECTIVE_MAX_LENGTH } from "../components/strategyObjectives";
import { DeckStatus } from "./DeckParts";
import { dayMonth, dayMonthYear, periodDay, periodYears, relativeDay, statusPillText } from "./deckModel";

export interface StrategyStartRequest {
  objective: string;
  file: File | null;
  modelId: string | null;
  reasoningEffort: ReasoningEffort;
}

export default function StrategyStart({
  brandName,
  strategies,
  canEdit,
  busy,
  error,
  onStart,
  onOpen,
}: {
  brandName: string | null;
  strategies: StrategyListItem[];
  canEdit: boolean;
  busy: boolean;
  error: string | null;
  onStart: (request: StrategyStartRequest) => void;
  onOpen: (strategy: StrategyListItem) => void;
}) {
  const brand = brandName ?? "the brand";
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  /* The same model and effort as the chat: one picker, one remembered pick. */
  const models = useChatModels();
  const persistSelection = useChatModelStore((s) => s.setSelection);
  const [model, setModel] = useState<ChatModel | null>(null);
  const [effort, setEffort] = useState<ReasoningEffort>(() => useChatModelStore.getState().reasoningEffort);
  const picker = useModelPicker({
    models: models.data ?? [],
    selectedModel: model,
    onModelChange: (next) => { setModel(next); persistSelection(next.id, effort); },
    selectedReasoningEffort: effort,
    onReasoningEffortChange: (next) => { setEffort(next); if (model) persistSelection(model.id, next); },
    disabled: busy,
    menuClassName: "sd-glow-menu",
  });
  const quiet = focused || !!text || !!file;
  const canSend = canEdit && !busy && !!text.trim();

  const choose = (next: File | null) => {
    if (next && !isImportableStrategyFile(next)) { setFileError("Export your PowerPoint or Keynote as a PDF, then add that file."); return; }
    if (next && next.size > IMPORT_MAX_BYTES) { setFileError("This PDF is over 25 MB. Add a compressed PDF."); return; }
    if (next && next.size === 0) { setFileError("This file is empty. Add another PDF."); return; }
    setFile(next);
    setFileError(null);
  };
  const send = () => {
    if (!canSend) return;
    onStart({ objective: text.trim(), file, modelId: model?.id ?? null, reasoningEffort: effort });
  };
  const sorted = useMemo(() => {
    const rank = { active: 0, ideas: 1, archived: 2 } as Record<string, number>;
    return [...strategies].sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || b.periodStart.localeCompare(a.periodStart));
  }, [strategies]);

  return (
    <div className="sd-start">
      <header className="sd-start-top">
        <h1>Strategy</h1>
        <span>{brandName}</span>
      </header>
      <div className="sd-start-hero" aria-label="A clear direction starts here.">
        <p>A clear direction</p>
        <p><mark>starts here.</mark></p>
      </div>

      {canEdit ? (
        <div className="sd-start-box">
          <div className="sd-glow-wrap">
          <form
            className="sd-glow"
            data-quiet={quiet || undefined}
            aria-busy={busy || undefined}
            onSubmit={(event) => { event.preventDefault(); send(); }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); choose(event.dataTransfer.files[0] ?? null); }}
          >
            <div className="sd-glow-field">
              <span className="sd-glow-pill"><img src={strategyPill} alt="" /><span>Strategy</span></span>
              <textarea
                ref={area}
                className="co-input-own-focus"
                value={text}
                rows={2}
                maxLength={OBJECTIVE_MAX_LENGTH}
                aria-label={`What should ${brand}'s strategy achieve?`}
                placeholder={`Next year’s strategy for ${brand}. Grow the community, not only the reach.`}
                disabled={busy}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
                }}
              />
              {file ? (
                <span className="sd-glow-file">
                  <FileText aria-hidden size={16} />
                  <span>{file.name}</span>
                  <small>{(file.size / 1024 / 1024).toFixed(1)} MB</small>
                  <button type="button" aria-label="Remove the PDF" onClick={() => choose(null)} disabled={busy}><X aria-hidden size={14} /></button>
                </span>
              ) : null}
            </div>
            <div className="sd-glow-controls">
              <input ref={input} type="file" accept="application/pdf,.pdf" hidden tabIndex={-1} onChange={(event) => { choose(event.target.files?.[0] ?? null); event.target.value = ""; }} />
              <button type="button" className="sd-glow-attach" aria-label="Add a PDF: a brief or a strategy you have" title="Add a PDF" onClick={() => input.current?.click()} disabled={busy}>
                <Plus aria-hidden size={16} />
              </button>
              {picker.chip}
              <span className="sd-push" />
              <button type="submit" className="sd-glow-send" aria-label="Start the strategy" data-ready={canSend || undefined} disabled={!canSend}>
                <ArrowUp aria-hidden size={16} />
              </button>
            </div>
          </form>
          {picker.menu}
          </div>
          {fileError ? <p role="alert" className="sd-start-error">{fileError}</p> : null}
          {error ? <p role="alert" className="sd-start-error">{error}</p> : null}
          <p className="sd-start-note">
            {busy
              ? file ? "Uploading your PDF. Creo starts the strategy once it is read…" : "Creo is starting the strategy…"
              : `Creo reads ${brandName ?? "the brand"}’s Universe, its documents and any PDF you add, then writes the strategy with you in the chat.`}
          </p>
        </div>
      ) : (
        <p className="sd-start-note">An editor starts a strategy here. You can read every strategy below.</p>
      )}

      {sorted.length ? (
        <section className="sd-start-list" aria-label={`${brandName ?? "The brand"}’s strategies`}>
          <div className="sd-start-head">
            <h2>{brandName ? `${brandName}’s strategies` : "Strategies"}</h2>
            <span>One is live at a time. Drafts and the next period wait here.</span>
          </div>
          {sorted.map((s) => {
            const live = s.status === "active";
            const day = live ? periodDay(s.periodStart, s.periodEnd) : null;
            const meta = live
              ? `Live since ${dayMonth(s.liveAt ?? s.updatedAt)}${day ? ` · ${day}` : ""}`
              : s.status === "archived"
                ? `Ran ${dayMonthYear(s.periodStart)} to ${dayMonthYear(s.periodEnd)}`
                : `Edited ${relativeDay(s.updatedAt)}`;
            return (
              <article key={s.id} className={cn("sd-start-card", live && "sd-start-card-live")}>
                <div className="sd-start-card-words">
                  <div>
                    <h3>{s.title || periodYears(s.periodStart, s.periodEnd)}</h3>
                    <DeckStatus>{statusPillText(s, { live: "LIVE" })}</DeckStatus>
                  </div>
                  <p>{meta}</p>
                </div>
                <KitButton variant="secondary" onClick={() => onOpen(s)}>{s.status === "draft" ? "Continue" : "Open"}</KitButton>
              </article>
            );
          })}
        </section>
      ) : null}
    </div>
  );
}
